import { useState, useEffect, useRef, useCallback } from "react";

/**
 * useUnreadTracker
 *
 * Contagem de não-lidas por sala, persistida em localStorage por
 * (usuário, sala) — cada aluno/professor tem sua própria marcação, mesmo
 * em navegador compartilhado. Conta só o que chegou enquanto o chat estava
 * fechado (não é um histórico completo de leitura).
 *
 * Deduplica por message id: no mobile é comum haver brevemente duas
 * conexões WS vivas (ex: volta de segundo plano reconecta antes da conexão
 * antiga fechar), o que entregaria a mesma mensagem duas vezes.
 */
export function useUnreadTracker({ roomId, currentUserId }) {
  const storageKey =
    roomId && currentUserId ? `chat_unread_${currentUserId}_${roomId}` : null;
  // Chave separada só pro flag de "tem @menção não lida" - contagem e menção
  // são sinais diferentes (uma é "tem mensagem nova", a outra é "alguém te
  // chamou direto"), cada uma com seu próprio badge no ícone do chat.
  const mentionStorageKey =
    roomId && currentUserId ? `chat_unread_mention_${currentUserId}_${roomId}` : null;

  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMentionUnread, setHasMentionUnread] = useState(false);
  const countedIdsRef = useRef(new Set());

  useEffect(() => {
    countedIdsRef.current = new Set();
    if (!storageKey) {
      setUnreadCount(0);
      setHasMentionUnread(false);
      return;
    }
    setUnreadCount(Number(localStorage.getItem(storageKey) || 0));
    setHasMentionUnread(localStorage.getItem(mentionStorageKey) === "1");
  }, [storageKey, mentionStorageKey]);

  const registerIncoming = useCallback(
    (messageId, isMention) => {
      if (!storageKey) return;
      if (messageId != null) {
        if (countedIdsRef.current.has(messageId)) return;
        countedIdsRef.current.add(messageId);
      }
      setUnreadCount((prev) => {
        const next = prev + 1;
        localStorage.setItem(storageKey, String(next));
        return next;
      });
      if (isMention) {
        localStorage.setItem(mentionStorageKey, "1");
        setHasMentionUnread(true);
      }
    },
    [storageKey, mentionStorageKey]
  );

  const markAsRead = useCallback(() => {
    if (!storageKey) return;
    localStorage.setItem(storageKey, "0");
    localStorage.setItem(mentionStorageKey, "0");
    setUnreadCount(0);
    setHasMentionUnread(false);
  }, [storageKey, mentionStorageKey]);

  return { unreadCount, hasMentionUnread, registerIncoming, markAsRead };
}
